import React from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PromptRibbon from "../components/Ribbon";

/* ===== Layout Shell ===== */
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: #fff;
`;
const Main = styled.main`
  flex: 1 0 auto;
  padding-top: clamp(60px, 7vw, 80px); /* space under fixed navbar */
`;

/* ===== Hero ===== */
const HeaderSection = styled.section`
  position: relative;
  overflow: clip;
  padding: clamp(2.25rem, 5vw, 4rem) 1rem clamp(2rem, 4vw, 3rem);
  background: radial-gradient(
    1000px 600px at 10% -10%,
    #4e89aa 0%,
    #2a5a7a 45%,
    #244d66 70%,
    #1e3e52 100%
  );
  color: #fff;
  isolation: isolate;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: auto;
    border-radius: 50%;
    filter: blur(48px);
    opacity: 0.25;
    pointer-events: none;
    transform: translateZ(0);
  }
  &::before {
    width: 520px;
    height: 520px;
    left: -160px;
    top: -180px;
    background: radial-gradient(
      circle at 30% 30%,
      #7fd1ff,
      #3dccc7 60%,
      transparent 70%
    );
  }
  &::after {
    width: 580px;
    height: 580px;
    right: -220px;
    bottom: -220px;
    background: radial-gradient(
      circle at 70% 70%,
      #ffb489,
      #ff7f50 55%,
      transparent 70%
    );
  }
`;

const HeaderContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 180px;

  @media (max-width: 600px) {
   max-height: 140px;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(0.75rem, 1.8vw, 0.85rem);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c7e7f7;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
`;
const PageTitle = styled.h1`
  margin: 0.6rem 0 0.4rem;
  font-weight: 800;
  line-height: 1.08;
  font-size: clamp(1.6rem, 5vw, 2.4rem);
`;
const PageSubtitle = styled.p`
  margin: 0.25rem 0 0;
  opacity: 0.95;
  font-size: clamp(0.95rem, 2.2vw, 1.05rem);
  color: #e8f6ff;
`;

/* ===== Body ===== */
const SectionWrap = styled.section`
  padding: clamp(1.5rem, 4vw, 2.5rem) 1rem;
  background: #f7f8fa;
`;
const Article = styled.article`
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #eef2f6;
  border-radius: 14px;
  padding: clamp(1rem, 3.5vw, 1.5rem);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);

  h2 {
    font-size: clamp(1.1rem, 3.5vw, 1.3rem);
    margin: 1rem 0 0.5rem;
    color: #111827;
  }
  p, li {
    color: #4b5563;
    line-height: 1.6;
    font-size: clamp(0.95rem, 2.4vw, 1rem);
  }
  ul {
    padding-left: 1.1rem;
    margin: 0.25rem 0 0.75rem;
  }
  hr {
    border: none;
    border-top: 1px solid #eef2f6;
    margin: 1rem 0;
  }
  .meta {
    color: #6b7280;
    font-size: 0.9rem;
  }
`;

const TermsofServices = () => {
  return (
    <>
      <Navbar />
      <PageShell>
        <Main>
          <HeaderSection>
            <HeaderContent>
              <Eyebrow>Legal</Eyebrow>
              <PageTitle>Terms of Service</PageTitle>
              <PageSubtitle>Last updated: October 06, 2025</PageSubtitle>
            </HeaderContent>
          </HeaderSection>

          <SectionWrap>
            <Article>
              <p className="meta">
                Welcome to AI Building Skills. These Terms of Service (“Terms”)
                govern your use of our website, content, courses, downloads, and
                services (“Services”). By accessing or using our Services, you
                agree to be bound by these Terms.
              </p>
              <hr />

              <h2>1. Use of Our Services</h2>
              <p>You must be at least 18 years old to access or use our Services. By using this site, you agree to:</p>
              <ul>
                <li>Use the Services only for lawful purposes</li>
                <li>Not copy, reproduce, or distribute our content without permission</li>
                <li>Not engage in any activity that interferes with or disrupts the Services</li>
              </ul>

              <h2>2. Intellectual Property</h2>
              <p>
                All content on this website — including but not limited to text, graphics,
                videos, course material, and downloadable resources — is the property of
                AI Building Skills and is protected by copyright and intellectual property laws.
              </p>
              <p>You may not:</p>
              <ul>
                <li>Resell or distribute our content without explicit written permission</li>
                <li>Use our materials for commercial gain unless licensed to do so</li>
              </ul>

              <h2>3. Purchases and Refunds</h2>
              <p>We may offer paid courses or services. When purchasing:</p>
              <ul>
                <li>You agree to provide accurate payment information</li>
                <li>All sales are final unless otherwise stated</li>
                <li>If a refund policy is offered, it will be clearly mentioned on the product page</li>
              </ul>

              <h2>4. Email Communications</h2>
              <p>
                By submitting your email through our forms, you agree to receive occasional
                updates, resources, and promotional emails. You can unsubscribe at any
                time using the link provided in our emails.
              </p>

              <h2>5. Disclaimer</h2>
              <p>
                Our content is provided for educational purposes only. While we aim for
                accuracy and usefulness, AI Building Skills makes no guarantees about the
                results you will achieve by using the information or tools we provide.
              </p>
              <p>
                We are not liable for any business decisions, delays, or losses resulting
                from use of our Services.
              </p>

              <h2>6. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, AI Building Skills will not be
                liable for any indirect, incidental, or consequential damages arising out
                of your use of the Services.
              </p>

              <h2>7. Changes to Terms</h2>
              <p>
                We reserve the right to update or modify these Terms at any time. Any
                changes will be effective upon posting on this page. We recommend
                reviewing this page periodically.
              </p>

              <h2>8. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact:
                <br />
                <strong>Abhishek Shete</strong>
                <br />
                <a href="mailto:abhisheks@aibuildingskills.com">
                  abhisheks@aibuildingskills.com
                </a>
              </p>
            </Article>
          </SectionWrap>
        </Main>

        <Footer />
        <PromptRibbon />
      </PageShell>
    </>
  );
};

export default TermsofServices;
